import { createSlice } from '@reduxjs/toolkit';

// Gift-voucher sale draft built in "Gift Voucher" billing mode. Lives in the
// store (not component state) because the left panel (builder), right panel
// (summary + approvals), mobile controls and the collect-payment screen are
// siblings, and the draft must survive switching back to Billing and in again.
let lineSeq = 0;
export const newLineKey = () => `gvl-${Date.now()}-${++lineSeq}`;

export const initialState = {
  saleId: null,            // server draft id once created
  buyer: null,             // { id, name, phone_number, email }
  buyerEmail: '',
  delivery: 'buyer',       // 'buyer' | 'individual'
  discount: null,          // { type: 'percentage' | 'amount', value, reason }
  lines: [],               // [{ key, typeId, value, qty, recipients: [{ name, email, phone, customer_id }] }]
  focusLineKey: null,
  server: null,            // last gift_voucher_sale payload from the API
  approvalsRequired: [],   // ['voucher_sale_discount', 'voucher_sale_quantity']
  approvalRequests: {},    // kind → { id, status }
  dirty: false,            // local edits not yet saved to the draft
  rev: 0,                  // bumped on every local edit; a save only clears `dirty` if nothing changed meanwhile
  syncError: null,
};

const touch = (state) => { state.dirty = true; state.syncError = null; state.rev += 1; };

const giftVoucherSaleSlice = createSlice({
  name: 'giftVoucherSale',
  initialState,
  reducers: {
    setGvBuyer: (state, action) => {
      state.buyer = action.payload;
      state.buyerEmail = action.payload?.email || '';
      touch(state);
    },
    setGvBuyerEmail: (state, action) => { state.buyerEmail = action.payload; touch(state); },
    setGvDelivery: (state, action) => { state.delivery = action.payload; touch(state); },
    setGvDiscount: (state, action) => {
      state.discount = action.payload;
      delete state.approvalRequests.voucher_sale_discount;
      touch(state);
    },
    addGvLine: (state, action) => {
      const line = { key: newLineKey(), recipients: [], qty: 1, ...action.payload };
      state.lines.push(line);
      state.focusLineKey = line.key;
      state.approvalRequests = {};
      touch(state);
    },
    updateGvLine: (state, action) => {
      const { key, changes } = action.payload;
      const line = state.lines.find((l) => l.key === key);
      if (!line) return;
      Object.assign(line, changes);
      if (line.recipients.length > line.qty) line.recipients = line.recipients.slice(0, line.qty);
      state.focusLineKey = key;
      if ('value' in changes || 'qty' in changes || 'typeId' in changes) state.approvalRequests = {};
      touch(state);
    },
    removeGvLine: (state, action) => {
      state.lines = state.lines.filter((l) => l.key !== action.payload);
      if (state.focusLineKey === action.payload) state.focusLineKey = state.lines[0]?.key || null;
      state.approvalRequests = {};
      touch(state);
    },
    replaceGvLines: (state, action) => {
      state.lines = action.payload;
      state.focusLineKey = action.payload[0]?.key || null;
      state.approvalRequests = {};
      touch(state);
    },
    setGvFocusLine: (state, action) => { state.focusLineKey = action.payload; },
    setGvServerSale: (state, action) => {
      const { sale, approvalsRequired, rev } = action.payload;
      state.server = sale;
      state.saleId = sale?.id ?? state.saleId;
      state.approvalsRequired = approvalsRequired || [];
      state.dirty = rev != null && rev !== state.rev;
      state.syncError = null;
    },
    setGvSyncError: (state, action) => { state.syncError = action.payload; },
    setGvApprovalRequest: (state, action) => {
      const { kind, id, status } = action.payload;
      state.approvalRequests[kind] = { id, status };
    },
    resetGvSale: () => initialState,
  },
});

export const {
  setGvBuyer,
  setGvBuyerEmail,
  setGvDelivery,
  setGvDiscount,
  addGvLine,
  updateGvLine,
  removeGvLine,
  replaceGvLines,
  setGvFocusLine,
  setGvServerSale,
  setGvSyncError,
  setGvApprovalRequest,
  resetGvSale,
} = giftVoucherSaleSlice.actions;

export default giftVoucherSaleSlice.reducer;

// Safe selector: stores built without this slice (e.g. tests) get an empty draft.
export const selectGvSale = (state) => state.giftVoucherSale || initialState;
